import {
  CheckIcon,
  SyncIcon,
  UploadIcon,
  XIcon
} from "@primer/octicons-react";
import {
  Box,
} from "@primer/react";
import { useCallback, useEffect, useState } from "react";
import TextTranslated from "../../components/text";
import Button from "../../components/ui/button";
import DataTable from "../../components/ui/data-table";
import { useCommonContext } from "../../contexts/common";
import { NotifyHelper } from "../../helpers/toast";

import { crmHocSinhBHYTApi } from "../../api/crmHocSinhBHYTApi";
import { ComboboxTruong } from "../../components/combobox-truong";

import HocSinhShortInfo from "../../components/hoc-sinh/HocSinhShortInfo";
import { useCommonSelectedHook } from "../../hooks/useCommonSelectedHook";

import ImportBHYTModal from "./import-bhyt-modal/ImportBHYTModal";
import { IHocSinhBHYTResponse } from "../../models/response/crm-hocsinh/IHocSinhBHYTResponse";
import HocSinhProfile from "../hoc-sinh-profile";

const BHYTPage = () => {
  const [isShowModal, setIsShowModal] = useState(false);
  const [selectedId, setSelectedId] = useState(0);
  const [filteredHSBHYT, setFilteredHSBHYTs] = useState<IHocSinhBHYTResponse[]>([]);
  const { translate } = useCommonContext();
  const [dm_truong_selected_id, setDmTruongSelectedId] = useState<number>(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isShowModalImport, setIsShowModalImport] = useState(false);
  const { nam_hoc, dm_coso_id } = useCommonSelectedHook();

  // Phân quyền API
  // const canView = checkAccesiableTo(LOP_API_ENDPOINT, "GET");
  // const canInsert = checkAccesiableTo(LOP_API_ENDPOINT, "POST");
  // const canUpdate = checkAccesiableTo(LOP_API_ENDPOINT, "PUT");
  // const canDelete = checkAccesiableTo(`${LOP_API_ENDPOINT}/{id}`, "DELETE");
  // const canViewDetail = checkAccesiableTo(`${LOP_API_ENDPOINT}/{id}`, "GET");
  const canView = true;
  const canUpdate = true;
  const sortData = (data: IHocSinhBHYTResponse[]) => {
    return data.sort((a, b) => {
      // Sắp xếp theo tên hệ trước
      const heComparison = (a.ten_he || "").localeCompare(b.ten_he || "", 'vi', { numeric: true });
      if (heComparison !== 0) {
        return heComparison;
      }
      // Nếu tên hệ giống nhau, sắp xếp theo tên lớp
      return (a.ten_lop || "").localeCompare(b.ten_lop || "", 'vi', { numeric: true });
    });
  };

  const handleReloadAsync = useCallback(async () => {
    if (!canView) return;
    try {
      setIsLoading(true);
      const res = await crmHocSinhBHYTApi.selectByNamHoc(dm_coso_id, nam_hoc, dm_truong_selected_id);
      if (res.is_success) {
        // Sắp xếp dữ liệu
        const sortedData = sortData(res.data);
        setFilteredHSBHYTs(sortedData);
      } else {
        NotifyHelper.Error(res.message ?? "");
      }
    } catch (error) {
      NotifyHelper.Error("Có lỗi xảy ra khi tải dữ liệu");
    } finally {
      setIsLoading(false);
    }
  }, [canView, nam_hoc, dm_coso_id, dm_truong_selected_id]);

  useEffect(() => {
    if (!canView) return;
    handleReloadAsync();
  }, [nam_hoc, dm_coso_id, dm_truong_selected_id, canView, handleReloadAsync]);



  // Kiểm tra quyền truy cập
  if (!canView) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", p: 4 }}>
        <TextTranslated value="Bạn không có quyền xem thông tin BHYT" />
      </Box>
    );
  }

  const handleCloseImportModal = () => {
    setIsShowModalImport(false);
    handleReloadAsync();
  }

  const handleEditClick = (id: number) => {
    setSelectedId(id);
    if (!canUpdate) {
      NotifyHelper.Error("Bạn không có quyền thêm mới/chỉnh sửa học sinh");
      return;
    }
    setIsShowModal(true); // Mở modal
  };

  return (
    <Box sx={{ p: 3 }}>
      <DataTable
        height={`${window.innerHeight - 240}px`}
         paging={{
          enable: true,
          pageSizeItems: [20, 50, 200, 500, 1000],
        }}
        actionComponent={
          <Box
            sx={{
              display: "flex",
              gap: 2,
            }}
          >
            <ComboboxTruong
              value={dm_truong_selected_id}
              onValueChanged={(value) => {
                setDmTruongSelectedId(value);
              }}
              isShowClearButton={true}
            />
            {canView && (
              <Button
                text="Base.Label.Refresh"
                leadingVisual={SyncIcon}
                size="medium"
                onClick={handleReloadAsync}
                disabled={isLoading}
              />
            )}
            {/* {canInsert && (
              <Button
                text={translate("Base.Label.Create")}
                leadingVisual={PlusIcon}
                variant="primary"
                size="medium"
                onClick={() => {
                  setIsShowModal(true);
                  setSelectedId(0);
                }}
                disabled={isLoading}
              />
            )} */}
            <Button
              text={"Import BHYT"}
              leadingVisual={UploadIcon}
              variant="default"
              size="medium"
              onClick={() => {
                setIsShowModalImport(true);
              }}
              disabled={isLoading}
            />
          </Box>
        }
        columns={[
          {
            dataField: "ma_hs",
            width: "110px",
            caption: translate("Mã học sinh"),
            fixed: true,
          },
          {
            dataField: "ho_ten",
            width: "200px",
            caption: translate("Học sinh"),
            cellRender: (data: IHocSinhBHYTResponse) => {
              return (
                <HocSinhShortInfo data={data} onClick={() => { handleEditClick(data.id) }} />
              );
            },
          },
          {
            dataField: "ma_hs",
            width: "0px",
            caption: "",
            isVisible: false,
          },
          {
            dataField: "ho_ten_en",
            width: "0px",
            caption: "",
            isVisible: false,
          },
          {
            dataField: "ten_lop",
            caption: translate("Trường lớp"),
            width: "250px",
            cellRender: (data: IHocSinhBHYTResponse) => {
              return (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "baseline",
                    // fontWeight: 600
                  }}
                >
                  <Box sx={{ display: "flex" }}>
                    <Box sx={{ fontWeight: 600 }}>{data.ten_lop}</Box>
                  </Box>
                  <Box sx={{ color: "fg.muted" }}>
                    {data.ten_khoi}, Hệ {data.ten_he}
                  </Box>
                </Box>
              );
            },
          },
          {
            dataField: "ten_khoi",
            width: "0px",
            caption: "",
            isVisible: false,
          },
          {
            dataField: "ten_he",
            width: "0px",
            caption: "",
            isVisible: false,
          },
          {
            dataField: "ten_truong",
            width: "0px",
            caption: "",
            isVisible: false,
          },
          {
            dataField: "so_dinh_danh",
            width: "100px",
            caption: "Số định danh",
            isVisible: true,
          },
          {
            dataField: "so_the_bhyt",
            width: "100px",
            caption: "Số thẻ bhyt",
            isVisible: true,
          },
          {
            dataField: "noi_dangky_khaisinh",
            width: "100px",
            caption: "Nơi đăng ký khai sinh",
            isVisible: true,
          },
          {
  dataField: "con_lttv_bodoi_congan",
  width: "100px",
  caption: "Con của LLVT - Bộ đội, Công an",
  isVisible: true,
  cellRender: (data: IHocSinhBHYTResponse) => {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100%",
        }}
      >
        {String(data.con_lttv_bodoi_congan) === "True" ? (
          <CheckIcon size={16} fill="#1a7f37" />
        ) : String(data.con_lttv_bodoi_congan) === "False" ? (
          <XIcon size={16} fill="#d1242f" />
        ) : null}
      </Box>
    );
  },
},
          {
            dataField: "ghi_chu",
            width: "100px",
            caption: "Ghi chú",
            isVisible: true,
          }
        ]}
        data={filteredHSBHYT}
        title="Học sinh BHYT"
        subTitle={`${translate("Base.Label.TotalCount")}: ${filteredHSBHYT.length
          } ${dm_coso_id ? "(Đã lọc theo cơ sở)" : ""}`}
        searchEnable
      />
      {isShowModal && canUpdate && (
        <HocSinhProfile
          id={selectedId}
          onClose={() => {
            handleReloadAsync();
            setIsShowModal(false);
          }}
          onSuccess={() => {
            handleReloadAsync();
            setIsShowModal(false);
          }}
        />
      )}
      {isShowModalImport && (
        <ImportBHYTModal
          onClose={handleCloseImportModal}
          animationOf={`#btnImportThiDauVao`}
        />
      )}
    </Box>
  );
};

export default BHYTPage;
